import {setSpellCheckEnabledState} from '@/reducers/user-preferences.reducer';
import {isFontLoaded} from '@Libraries/font-library';
import {parsePrimaryFontFamily} from '@Utils/font.util';

export const SQUIGGLY_LINE_ERROR_COLOR = '#FF0000';
export const TEXT_HOVER_HIGHLIGHT = 'rgba(211, 37, 89, 0.1)';

export interface UserDictionary {
  total: number;
  dict: string[];
}

const spellCheckCache: Record<string, boolean> = {};
const suggestedWordsCache: Record<string, string[]> = {};
let suggestWordsInProgress = false;

export const updateSpellCheckCache = (word: string, value: boolean): void => {
  spellCheckCache[word] = value;
};

export const getUserDict = async (offset = 0): Promise<UserDictionary | []> => {
  const response = (await window.PMW.readLocal('spellcheck/getUserDictionary', {
    offset,
  })) as UserDictionary;
  return response || [];
};

/**
 * Adds word to user dictionary
 * @param {string} text
 * @returns {boolean}
 */
export const addToDictionary = async (text: string): Promise<boolean> => {
  if (window.PMW.getUserPremiumLevel()) {
    try {
      return (await window.PMW.writeLocal('spellcheck/ignoreWord', {
        text,
      })) as boolean;
    } catch {
      console.error('ignore word failed');
    }
  }
  return false;
};

/**
 * Remove word from the user dictionary
 * @param {string} word
 * @returns {boolean}
 */
export const removeFromUserDictionary = async (word: string): Promise<boolean> => {
  try {
    return (await window.PMW.writeLocal('spellcheck/removeFromDictionary', {
      word,
    })) as boolean;
  } catch {
    throw new Error('user dictionary update failed');
  }
};

/**
 * Checks spell and returns response using pspell checker
 * @param {string} text
 */
export const checkSpell = async (text: string | string[]): Promise<Record<string, boolean>> => {
  const unchecked = [];
  for (const word of text) {
    if (word.length && spellCheckCache[word] === undefined) {
      unchecked.push(word);
    }
  }
  if (unchecked.length) {
    const data = (await window.PMW.readLocal('spellcheck/checkSpell', {
      text: unchecked,
      lang: window.i18next.language,
    })) as Record<string, boolean>;
    for (const [word, isSpelledCorrectly] of Object.entries(data)) {
      if (!spellCheckCache[word]) {
        spellCheckCache[word] = isSpelledCorrectly;
      }
    }
  }
  return spellCheckCache;
};

export const suggestWords = async (wordToCheck: string): Promise<string[]> => {
  if (wordToCheck in spellCheckCache && spellCheckCache[wordToCheck]) {
    return [];
  }
  if (suggestedWordsCache[wordToCheck]) {
    return suggestedWordsCache[wordToCheck];
  }
  if (getIsSpellCheckEnabledFromStore() && !suggestWordsInProgress) {
    suggestWordsInProgress = true;
    const unchecked = [];
    for (const [word, correct] of Object.entries(spellCheckCache)) {
      if (!correct && suggestedWordsCache[word] === undefined) {
        unchecked.push(word);
      }
    }
    if (unchecked.length && window.i18next.language) {
      const data = (await window.PMW.readLocal('spellcheck/suggestWords', {
        text: unchecked,
        lang: window.i18next.language,
      })) as Record<string, string[]>;
      for (const [word, suggestedWords] of Object.entries(data)) {
        if (!suggestedWordsCache[word] && suggestedWords) {
          suggestedWordsCache[word] = suggestedWords.slice(0, 2);
        } else {
          suggestedWordsCache[word] = [];
        }
      }
    }
    suggestWordsInProgress = false;
    return suggestedWordsCache[wordToCheck];
  }
  return [];
};

export const updateIsSpellCheckEnabledForUser = async (isSpellcheckEnabled: boolean): Promise<void> => {
  await setSpellCheckEnabledState(isSpellcheckEnabled);
  await window.posterEditor?.whiteboard?.getCurrentPage().spellCheck.checkSpellForSelectedObject();
};

export const getIsSpellCheckEnabledFromStore = (): boolean => {
  return window.PMW.redux.store.getState().userPreferences.isSpellCheckAvailable && window.PMW.redux.store.getState().userPreferences.isSpellCheckEnabled;
};

const singleCaseOnlyFontCache = new Map<string, boolean>();
const GLYPH_SAMPLE_FONT_SIZE = 100;
const GLYPH_CANVAS_SIZE = GLYPH_SAMPLE_FONT_SIZE * 2;
const GLYPH_GRID_SIZE = 32;
const GLYPH_INK_ALPHA_THRESHOLD = 40;
const RGBA_CHANNELS = 4;
const ALPHA_CHANNEL_OFFSET = 3;
const GLYPH_LEFT_PADDING_FACTOR = 0.2;
const CASE_DISTINCT_LETTERS = 'abdefghmnqrt';
const GLYPH_SHAPE_MATCH_RATIO = 0.9;
const SINGLE_CASE_LETTER_MATCH_RATIO = 0.75;

let glyphCanvasContext: CanvasRenderingContext2D | null = null;

export const isSingleCaseOnlyFont = (fontFamily: string): boolean => {
  const family = parsePrimaryFontFamily(fontFamily);
  const cached = singleCaseOnlyFontCache.get(family);
  if (cached !== undefined) {
    return cached;
  }
  const result = detectSingleCaseOnlyFont(family);
  if (result === null) {
    return false;
  }
  singleCaseOnlyFontCache.set(family, result);
  return result;
};

const detectSingleCaseOnlyFont = (fontFamily: string): boolean | null => {
  if (!isFontLoaded(fontFamily) || !document.fonts.check(`${GLYPH_SAMPLE_FONT_SIZE}px "${fontFamily}"`)) {
    return null;
  }
  const context = getGlyphCanvasContext();
  if (!context) {
    return null;
  }
  let comparable = 0;
  let matching = 0;
  for (const lowerChar of CASE_DISTINCT_LETTERS) {
    const lowerShape = getNormalizedGlyphShape(context, lowerChar, fontFamily);
    const upperShape = getNormalizedGlyphShape(context, lowerChar.toUpperCase(), fontFamily);
    if (!lowerShape || !upperShape) {
      continue;
    }
    comparable += 1;
    if (getShapeSimilarity(lowerShape, upperShape) >= GLYPH_SHAPE_MATCH_RATIO) {
      matching += 1;
    }
  }
  if (comparable === 0) {
    return false;
  }
  return matching / comparable >= SINGLE_CASE_LETTER_MATCH_RATIO;
};

const getGlyphCanvasContext = (): CanvasRenderingContext2D | null => {
  if (glyphCanvasContext) {
    return glyphCanvasContext;
  }
  const canvas = document.createElement('canvas');
  canvas.width = GLYPH_CANVAS_SIZE;
  canvas.height = GLYPH_CANVAS_SIZE;
  glyphCanvasContext = canvas.getContext('2d', {willReadFrequently: true});
  return glyphCanvasContext;
};

const getNormalizedGlyphShape = (context: CanvasRenderingContext2D, char: string, fontFamily: string): Uint8Array | null => {
  context.clearRect(0, 0, GLYPH_CANVAS_SIZE, GLYPH_CANVAS_SIZE);
  context.font = `${GLYPH_SAMPLE_FONT_SIZE}px "${fontFamily}"`;
  context.textBaseline = 'middle';
  context.fillText(char, GLYPH_SAMPLE_FONT_SIZE * GLYPH_LEFT_PADDING_FACTOR, GLYPH_CANVAS_SIZE / 2);
  const {data} = context.getImageData(0, 0, GLYPH_CANVAS_SIZE, GLYPH_CANVAS_SIZE);

  let minX = GLYPH_CANVAS_SIZE;
  let minY = GLYPH_CANVAS_SIZE;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < GLYPH_CANVAS_SIZE; y++) {
    for (let x = 0; x < GLYPH_CANVAS_SIZE; x++) {
      if (data[(y * GLYPH_CANVAS_SIZE + x) * RGBA_CHANNELS + ALPHA_CHANNEL_OFFSET] > GLYPH_INK_ALPHA_THRESHOLD) {
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
      }
    }
  }
  if (maxX < 0) {
    return null;
  }
  const boxWidth = maxX - minX + 1;
  const boxHeight = maxY - minY + 1;
  const shape = new Uint8Array(GLYPH_GRID_SIZE * GLYPH_GRID_SIZE);
  for (let gridY = 0; gridY < GLYPH_GRID_SIZE; gridY++) {
    for (let gridX = 0; gridX < GLYPH_GRID_SIZE; gridX++) {
      const sampleX = minX + Math.floor((gridX / GLYPH_GRID_SIZE) * boxWidth);
      const sampleY = minY + Math.floor((gridY / GLYPH_GRID_SIZE) * boxHeight);
      shape[gridY * GLYPH_GRID_SIZE + gridX] = data[(sampleY * GLYPH_CANVAS_SIZE + sampleX) * RGBA_CHANNELS + ALPHA_CHANNEL_OFFSET] > GLYPH_INK_ALPHA_THRESHOLD ? 1 : 0;
    }
  }
  return shape;
};

const getShapeSimilarity = (a: Uint8Array, b: Uint8Array): number => {
  let matchingCells = 0;
  for (let i = 0; i < a.length; i++) {
    if (a[i] === b[i]) {
      matchingCells += 1;
    }
  }
  return matchingCells / a.length;
};
